import React from "react";
import { canShowField, findAvailableFields, IPropertyEditorProps } from "../common";
import { IDataSource, IDataSourceBinding } from "../data-source-picker/datasource";
import { CustomColorPicker, escapeUnit, Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyGroupTitle, PropertyTitle, RemoveButton } from "../ui-props/common";
import { defaultMapConfigs, IMapConfigProps } from "./MapUI";
import { AutoCompleteInput, Checkbox, ColorPicker, Input, Label, ToggleFilter, Tooltip } from "uxp/components";
import { appendRowToContextData, IContextData } from "../../../hooks/useContextData";
import { WidgetDesignerPillInput } from "../pill-input/PillInput";
import { DataSourcePropertyEditor } from "../ui-props/DataSourceProperty";
import { Conditional } from "../ConditionalComponent";
import ActionEditorLink from "../action-editor/ActionEditor";
import { FAIcon as FontAwesomeIcon } from '../components/FAIcon';
import { FieldProperty, IFieldTitleProps } from "../components/FieldTitle";
import { MapSourceEditor } from "./MapSourceEditor";
import jp from 'jsonpath';
import './MapPropertyEditor.scss'

interface IFieldProps {
    locationText?: string
    fontSize?: string;
    iconColor?: string;
    fieldLabel?: IFieldTitleProps
    mapWidth?: string
    mapHeight?: string
}
interface IMapProperty {
    source: IDataSourceBinding,
    mapConfig: IMapConfigProps,
    refreshInterval: string,
}

interface MapPropertyEditorProps extends IMapProperty, IFieldProps { }

export const MapPropertyEditor: React.FunctionComponent<IPropertyEditorProps<MapPropertyEditorProps>> = (props) => {
    const { hideFields } = props
    const _props: MapPropertyEditorProps = {
        source: null,
        locationText: '',
        iconColor: '#424242',
        fontSize: '14px',
        refreshInterval: '2000',
        ...props.uiProps,
        mapConfig: { ...defaultMapConfigs, ...props?.uiProps?.mapConfig }
    };
    let datasources: IDataSource[] = props.parentUIProps?.datasources || [];

    let possibleValues = {};
    findAvailableFields(props.parentUIProps.source, props.parentUIProps.datasources, possibleValues);
    let pv = Object.keys(possibleValues);
    pv.sort();

    const _contextData = appendRowToContextData(props.contextData, { uiProps: props.parentUIProps } as any, _props?.source)

    console.log('____context_data___', _contextData, _props)
    return <>

        <PropertyContainer>

            <Conditional visible={canShowField('fieldLabel', hideFields)} >
                <PropertyGroup>
                    <PropertyGroupTitle>Field Label</PropertyGroupTitle>
                    <FieldProperty uiProps={props.uiProps} onChange={props.onChange} />
                </PropertyGroup>
            </Conditional>

            <Conditional visible={canShowField('mapFieldHeight', hideFields)} >
                <PropertyGroup>
                    <PropertyGroupTitle>Map Width / Height</PropertyGroupTitle>
                    <Property>
                        <PropertyTitle> Width </PropertyTitle>
                        <WidgetDesignerPillInput
                            value={_props?.mapWidth || '100%'}
                            onChange={s => props.onChange(Object.assign({}, _props, { mapWidth: s }))}
                            contextData={_contextData}
                        />
                    </Property>

                    <Property>
                        <PropertyTitle> Height </PropertyTitle>
                        <WidgetDesignerPillInput
                            value={_props?.mapHeight || '150px'}
                            onChange={s => props.onChange(Object.assign({}, _props, { mapHeight: s }))}
                            contextData={_contextData}
                        />
                    </Property>
                </PropertyGroup>
            </Conditional>

        </PropertyContainer >

        <PropertyContainer>
            <PropertyContainerTitle>Map Properties</PropertyContainerTitle>

            <PropertyGroup>
                <PropertyGroupTitle>Tile Provider</PropertyGroupTitle>
                <Property>

                    <MapSourceEditor
                        mapTileSource={_props?.mapConfig?.mapTileSource}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.mapTileSource', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />

                </Property>
            </PropertyGroup>

            <PropertyGroup>
                <PropertyGroupTitle> Center Position</PropertyGroupTitle>
                <Property>
                    <PropertyTitle>Longitude/X</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.mapConfig?.initialX?.toString() || ''}
                        placeholder='Center X'
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.initialX', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    /></Property>
                <Property>
                    <PropertyTitle>Latitude/Y</PropertyTitle>
                    <WidgetDesignerPillInput
                        placeholder='Center Y'
                        value={_props?.mapConfig?.initialY?.toString() || ''}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.initialY', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>
            </PropertyGroup>

            <PropertyGroup>
                <PropertyGroupTitle>Zoom</PropertyGroupTitle>
                <Property>
                    <PropertyTitle>Minimum Zoom</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.mapConfig?.minZoom?.toString() || ''}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.minZoom', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>
                <Property>
                    <PropertyTitle>Initial Zoom</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.mapConfig?.initialZoom?.toString() || ''}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.initialZoom', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>
                <Property>
                    <PropertyTitle>Maximum Zoom</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.mapConfig.maxZoom?.toString() || ''}
                        onChange={s => {

                            jp.apply(_props, '$.mapConfig.maxZoom', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>
            </PropertyGroup>

        </PropertyContainer>

        <PropertyContainer>
            <PropertyContainerTitle>Markers</PropertyContainerTitle>

            <PropertyGroup>
                <Property>
                    <DataSourcePropertyEditor
                        binding={_props?.source || null}
                        context={props.uxpContext}
                        onChange={(binding) => props.onChange(Object.assign({}, _props, { source: binding }))}
                        onChangeAny={(p, v) => {
                            props.onChangeAny(p, v);
                        }}
                        containerId={props.containerId}
                        sources={datasources}
                        target={'dictionary-array'}
                        title='Data Source'
                        contextData={_contextData}
                        className='uxp-designer-data-source-selector'
                        designer={props.designer}
                    />
                </Property>
            </PropertyGroup>

            <PropertyGroup>
                <PropertyGroupTitle>Marker Fields</PropertyGroupTitle>

                <Property styles={{ padding: '2px' }} >
                    <Conditional visible={!_props?.mapConfig?.autoFillIdField}>
                        <Property>
                            <PropertyTitle>Longitude/X</PropertyTitle>
                            <WidgetDesignerPillInput
                                value={_props?.mapConfig?.xField}
                                onChange={s => {
                                    jp.apply(_props, '$.mapConfig.xField', () => (s))
                                    props.onChange(_props)
                                }}
                                contextData={_contextData}
                            />
                        </Property>

                        <Property>
                            <PropertyTitle>Latitude/Y</PropertyTitle>
                            <WidgetDesignerPillInput
                                value={_props?.mapConfig?.yField}
                                onChange={s => {
                                    jp.apply(_props, '$.mapConfig.yField', () => (s))
                                    props.onChange(_props)
                                }}
                                contextData={_contextData}
                            />
                        </Property>
                    </Conditional>

                    <Property>
                        <PropertyTitle>Marker Image</PropertyTitle>
                        <WidgetDesignerPillInput
                            value={_props?.mapConfig?.markerImageField}
                            onChange={s => {
                                jp.apply(_props, '$.mapConfig.markerImageField', () => (s))
                                props.onChange(_props)
                            }}
                            contextData={_contextData}
                        />
                    </Property>

                    <Property>
                        <PropertyTitle>Marker Tooltip Text (Markdown supported)</PropertyTitle>

                        <WidgetDesignerPillInput
                            value={_props?.mapConfig?.markerTooltipTextField}
                            onChange={s => {
                                jp.apply(_props, '$.mapConfig.markerTooltipTextField', () => (s))
                                props.onChange(_props)
                            }}
                            contextData={_contextData}
                        />
                    </Property>

                    <Property>
                        <PropertyTitle>Marker Size</PropertyTitle>
                        <Input
                            type="number"
                            value={_props?.mapConfig?.markerSize?.toString() || ''}
                            onChange={s => {
                                jp.apply(_props, '$.mapConfig.markerSize', () => (Number(s) || 0))
                                props.onChange(_props)
                            }}
                        />
                    </Property>
                </Property>

                <Property>
                    <PropertyTitle>On click an item </PropertyTitle>
                    <ActionEditorLink
                        uiProps={props.parentUIProps}
                        designer={props.designer}
                        uxpContext={props.uxpContext}
                        action={_props?.mapConfig?.onClickAction}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.onClickAction', () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>

            </PropertyGroup>

            <PropertyGroup>
                <PropertyGroupTitle>Marker Colors</PropertyGroupTitle>


                <Property>
                    <PropertyTitle>Default Marker Color</PropertyTitle>
                    <CustomColorPicker contextData={_contextData}
                        color={_props?.mapConfig?.defaultMarkerColor || 'white'}
                        onChange={s => {
                            jp.apply(_props, '$.mapConfig.defaultMarkerColor', () => s)
                            props.onChange(Object.assign({}, _props));
                        }}

                    />
                </Property>

                <Property>
                    <Checkbox
                        checked={!!_props?.mapConfig?.useValueForMarkerColor}
                        onChange={s => {
                            console.log("change", s, _props)
                            jp.apply(_props, '$.mapConfig.useValueForMarkerColor', () => s)
                            props.onChange(Object.assign({}, _props));
                        }}
                        label={"Use Value For Marker Color"}
                        type="bordered"
                    />

                </Property>


                {!!_props?.mapConfig?.useValueForMarkerColor ?
                    <>
                        <Property>
                            <PropertyTitle>Value Field</PropertyTitle>
                            <WidgetDesignerPillInput
                                value={_props?.mapConfig?.valueField}
                                onChange={s => {
                                    jp.apply(_props, '$.mapConfig.valueField', () => (s))
                                    props.onChange(_props)
                                }}
                                contextData={_contextData}
                            />
                        </Property>

                        <Property>

                            <div className="wd-ui-property-group-title marker-color-config" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Marker Color Configuration

                                <div
                                    className="plus"
                                    onClick={() => {
                                        let _colorConfig = _props?.mapConfig?.colorConfig || []
                                        _colorConfig.unshift({ from: null, to: null, color: '' })

                                        jp.apply(_props, `$.mapConfig.colorConfig`, () => (_colorConfig))
                                        props.onChange(_props)
                                    }}
                                >
                                    <FontAwesomeIcon icon={['fas', 'plus']} />
                                </div>
                            </div>

                            <div className="wd-item-list-config-value-colors">

                                {_props?.mapConfig?.colorConfig.map((s, i) => {

                                    return <div className="row">
                                        <div className="from">
                                            <Input
                                                type="number"
                                                placeholder='From'
                                                value={s?.from?.toString() || ''}
                                                onChange={(s) => {
                                                    jp.apply(_props, `$.mapConfig.colorConfig[${i}].from`, () => (Number(s)))
                                                    props.onChange(_props)
                                                }}
                                                className="value-input"
                                            />
                                        </div>
                                        <div style={{ fontWeight: 'var(--fontWeightBlack)' }}>{"-"}</div>
                                        <div className="to">
                                            <Input
                                                type="number"
                                                placeholder='To'
                                                value={s?.to?.toString() || ''}
                                                onChange={(s) => {
                                                    jp.apply(_props, `$.mapConfig.colorConfig[${i}].to`, () => (Number(s)))
                                                    props.onChange(_props)
                                                }}
                                                className="value-input"
                                            />
                                        </div>
                                        <div className="color">
                                            <ColorPicker
                                                color={s.color}
                                                onChange={(s) => {
                                                    jp.apply(_props, `$.mapConfig.colorConfig[${i}].color`, () => (s))
                                                    props.onChange(_props)
                                                }}
                                                className='color-input'
                                                position='right'
                                            />
                                        </div>

                                        <div className="close">
                                            <RemoveButton onClick={() => {
                                                let _colorConfig = _props?.mapConfig?.colorConfig || []
                                                _colorConfig.splice(i, 1)

                                                jp.apply(_props, `$.mapConfig.colorConfig`, () => (_colorConfig))
                                                props.onChange(_props)
                                            }} />
                                        </div>
                                    </div>
                                })}
                            </div>
                        </Property>
                    </>
                    :
                    <>
                        <Property>
                            <PropertyTitle>Status Field</PropertyTitle>
                            <AutoCompleteInput
                                value={_props?.mapConfig?.statusField}
                                onChange={s => {
                                    jp.apply(_props, '$.mapConfig.statusField', () => (s))
                                    props.onChange(_props)
                                }}
                                options={pv}
                                className="widget-designer-input"
                            />
                        </Property>
                        <Property>

                            <div className="wd-ui-property-group-title marker-color-config" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                Marker Color Configuration

                                <div
                                    className="plus"
                                    onClick={() => {
                                        let _status = _props?.mapConfig?.statusConfig || []
                                        _status.unshift({ status: 'status', color: '' })

                                        jp.apply(_props, `$.mapConfig.statusConfig`, () => (_status))
                                        props.onChange(_props)
                                    }}
                                >
                                    <FontAwesomeIcon icon={['fas', 'plus']} />
                                </div>
                            </div>

                            <div className="wd-item-list-config-status-colors">

                                {_props?.mapConfig?.statusConfig.map((s, i) => {

                                    return <div className="row">
                                        <div className="status">
                                            <Input
                                                value={s.status}
                                                onChange={(s) => {
                                                    jp.apply(_props, `$.mapConfig.statusConfig[${i}].status`, () => (s))
                                                    props.onChange(_props)
                                                }}
                                                className="status-input"
                                            />
                                        </div>
                                        <div className="color">
                                            <ColorPicker
                                                color={s.color}
                                                onChange={(s) => {
                                                    jp.apply(_props, `$.mapConfig.statusConfig[${i}].color`, () => (s))
                                                    props.onChange(_props)
                                                }}
                                                className='color-input'
                                                position='right'
                                            />
                                        </div>

                                        <div className="close">
                                            <RemoveButton onClick={() => {
                                                let _statusConfig = _props?.mapConfig?.statusConfig || []
                                                _statusConfig.splice(i, 1)

                                                jp.apply(_props, `$.mapConfig.statusConfig`, () => (_statusConfig))
                                                props.onChange(_props)
                                            }} />
                                        </div>
                                    </div>
                                })}
                            </div>
                        </Property>
                    </>

                }

            </PropertyGroup>

            <PropertyGroup>
                <div className="map-widget-autofill-marker-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className='wd-ui-property-group-title title'>Map data to a point</div>
                    <Conditional visible={_props?.mapConfig?.autoFillIndexBeingMarkedOnTheMap == null}>
                        <div className="plus"
                            onClick={() => {
                                let _autoFillMarkersConfig = _props?.mapConfig?.autoFillMarkersConfig || []
                                _autoFillMarkersConfig.unshift({ id: 'dummyId', autoFillX: 0, autoFillY: 0 })

                                jp.apply(_props, `$.mapConfig.autoFillMarkersConfig`, () => (_autoFillMarkersConfig))
                                props.onChange(_props)
                            }}
                        >
                            <FontAwesomeIcon icon={['fas', 'plus']} />
                        </div>
                    </Conditional>

                </div>

                <Property>
                    <PropertyTitle>Matching ID Field</PropertyTitle>
                    <AutoCompleteInput
                        value={_props?.mapConfig?.autoFillIdField}
                        onChange={s => {
                            jp.apply(_props, `$.mapConfig.autoFillIdField`, () => (s))
                            props.onChange(_props)
                        }}
                        options={pv}
                        className="widget-designer-input"
                    />
                </Property>

                <Conditional visible={_props?.mapConfig?.autoFillIndexBeingMarkedOnTheMap != null}>
                    <Property>
                        <Label>Please mark the point on the map...</Label>
                    </Property>
                </Conditional>

                <Conditional visible={_props?.mapConfig?.autoFillMarkersConfig && _props?.mapConfig?.autoFillMarkersConfig?.length > 0 && _props?.mapConfig?.autoFillIndexBeingMarkedOnTheMap == null}>
                    <Property>
                        <PropertyTitle>Matching ID</PropertyTitle>

                        <div className="map-widget-autofill-marker-config-panel">

                            {_props?.mapConfig?.autoFillMarkersConfig.map((m, i) => {

                                return <div className="row">
                                    <div className="id">
                                        <Input
                                            value={m?.id}
                                            onChange={(s) => {
                                                jp.apply(_props, `$.mapConfig..autoFillMarkersConfig[${i}].id`, () => (s))
                                                props.onChange(_props)
                                            }}
                                            className="id-input"
                                        />
                                    </div>

                                    <Tooltip
                                        position={'left'}
                                        content={() => (
                                            <div><b>
                                                {
                                                    (m.autoFillX && m.autoFillY) ? `${m.autoFillX}, ${m.autoFillY}` : "Mark coordinates on the map"
                                                }
                                            </b></div>
                                        )}>
                                        <div className="crosshair" onClick={() => {
                                            jp.apply(_props, `$.mapConfig..autoFillIndexBeingMarkedOnTheMap`, () => (i))
                                            props.onChange(_props)
                                        }}>
                                            <div className="crosshair-icon"></div>
                                        </div>
                                    </Tooltip>

                                    <div className="close">
                                        <RemoveButton onClick={() => {
                                            let _autoFillMarkersConfig = _props?.mapConfig?.autoFillMarkersConfig || []
                                            _autoFillMarkersConfig.splice(i, 1)

                                            jp.apply(_props, `$.mapConfig.autoFillMarkersConfig`, () => (_autoFillMarkersConfig))
                                            props.onChange(_props)
                                        }} />
                                    </div>
                                </div>
                            })}
                        </div>

                    </Property>
                </Conditional>
                <p style={{ padding: '5px', fontSize: '11px' }}><b>Note: </b>Data with matching ids will have their X and Y values autofilled as configured above</p>
            </PropertyGroup>

            <PropertyGroup>
                <PropertyGroupTitle>Center Marker</PropertyGroupTitle>

                <Property>
                    <Checkbox
                        checked={_props?.mapConfig?.showCenterMarker || false}
                        onChange={s => {
                            jp.apply(_props, `$.mapConfig.showCenterMarker`, () => (s))
                            props.onChange(_props)
                        }}
                        type='bordered'
                        label="Show marker"
                    />
                </Property>

                <Property>
                    <PropertyTitle>Marker Color</PropertyTitle>
                    <CustomColorPicker
                        color={_props?.mapConfig?.centerMarkerColor || '#7AC783'}
                        onChange={s => {
                            jp.apply(_props, `$.mapConfig.centerMarkerColor`, () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>

                <Property>
                    <PropertyTitle>Marker Icon</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.mapConfig?.centerMarkerIcon || ''}
                        onChange={s => {
                            jp.apply(_props, `$.mapConfig.centerMarkerIcon`, () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>


            </PropertyGroup>

            <Conditional visible={canShowField('locationLabel', hideFields)} >

                <PropertyGroup>
                    <PropertyGroupTitle>Location Label</PropertyGroupTitle>

                    <Property>
                        <PropertyTitle>Location Text</PropertyTitle>
                        <WidgetDesignerPillInput
                            value={_props?.locationText || ''}
                            onChange={s => {
                                jp.apply(_props, `$.locationText`, () => (s))
                                props.onChange(_props)
                            }}
                            contextData={_contextData}
                        />
                    </Property>

                    <Property>
                        <PropertyTitle>Label Color</PropertyTitle>
                        <CustomColorPicker
                            color={_props?.iconColor || '#424242'}
                            onChange={s => {
                                jp.apply(_props, `$.iconColor`, () => (s))
                                props.onChange(_props)
                            }}
                            contextData={_contextData}
                        />
                    </Property>

                    <Property>
                        <PropertyTitle>Label Font Size</PropertyTitle>
                        <Input
                            type='number'
                            value={escapeUnit(_props.fontSize || '', 'px')}
                            onChange={s => {
                                let num = Number(s) || 0
                                if (num < 8) s = '8'
                                else if (num > 200) s = '200'

                                jp.apply(_props, `$.fontSize`, () => (s + 'px'))
                                props.onChange(_props)
                            }}
                            className="widget-designer-input"
                            inputAttr={{
                                max: '200',
                                min: '8'
                            }}
                        />
                    </Property>

                </PropertyGroup>

            </Conditional>

            <PropertyGroup>
                <Property>
                    <PropertyTitle>Refresh interval</PropertyTitle>
                    <WidgetDesignerPillInput
                        value={_props?.refreshInterval || '2000'}
                        onChange={s => {
                            jp.apply(_props, `$.refreshInterval`, () => (s))
                            props.onChange(_props)
                        }}
                        contextData={_contextData}
                    />
                </Property>
            </PropertyGroup>

        </PropertyContainer >

    </>;
}
